import React, { useLayoutEffect, useRef, useState } from 'react';
import type { PlanEdge } from '../types';
import { useCanvas, useNodeBox } from './canvasStore';
import { bezierPath, handlePosition } from './geometry';
import { useRuntime } from './runtime';

/** A path between two measured nodes, or null while either is unmeasured. */
const useEdgePath = (sourceId: string, targetId: string) => {
  const s = useNodeBox(sourceId);
  const t = useNodeBox(targetId);
  // Trap 3, edges before measure: an unmeasured end has no handle to point at yet.
  if (!s?.measured || !t?.measured) return null;
  const a = handlePosition(s, 'right');
  const b = handlePosition(t, 'left');
  return bezierPath(a.x, a.y, b.x, b.y);
};

const Edge = React.memo(({ edge, markerId, name }: { edge: PlanEdge; markerId: string; name: string }) => {
  const rt = useRuntime();
  const selected = useCanvas((s) => s.edgeSelection.has(edge.id));
  const geo = useEdgePath(edge.source_id, edge.target_id);
  if (!geo) return null;
  return (
    <g data-edge-id={edge.id} className={selected ? 'np-edge-selected' : undefined}>
      <path className="np-edge" d={geo.path} markerEnd={`url(#${markerId}${selected ? '-sel' : ''})`} />
      {/* Trap 8, a 1.6px stroke is unclickable: a transparent 20px twin takes the pointer.
          It is also the keyboard stop: Enter on it opens the arrow's menu (Canvas onKeyDown). */}
      <path
        className="np-edge-hit"
        d={geo.path}
        tabIndex={0}
        role="button"
        aria-haspopup="menu"
        aria-pressed={selected}
        aria-label={name}
        onClick={(e) => rt.selection.clickEdge(edge.id, e)}
        onKeyDown={(e) => {
          if (e.key === ' ') {
            e.preventDefault();
            rt.selection.clickEdge(edge.id, e);
          }
        }}
      />
    </g>
  );
});

const EdgeLabel = React.memo(({ edge, onEdit }: { edge: PlanEdge; onEdit?: (id: string) => void }) => {
  const rt = useRuntime();
  const geo = useEdgePath(edge.source_id, edge.target_id);
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const measured = !!geo;
  useLayoutEffect(() => {
    const el = ref.current;
    setTruncated(!!el && el.scrollWidth > el.clientWidth);
  }, [edge.label, measured]);
  if (!geo) return null;
  return (
    <div
      ref={ref}
      data-edge-id={edge.id}
      title={truncated ? edge.label : undefined}
      className="np-edge-label"
      style={{ transform: `translate(${geo.labelX}px, ${geo.labelY}px) translate(-50%, -50%)` }}
      onClick={(e) => rt.selection.clickEdge(edge.id, e)}
      onDoubleClick={onEdit ? () => onEdit(edge.id) : undefined}
    >
      {edge.label}
    </div>
  );
});

/** The label being written: a filled pill field at the arrow's midpoint, in the label layer so it pans and zooms with the arrow. */
const EdgeLabelEditor: React.FC<{
  edge: PlanEdge;
  name: string;
  onSave: (id: string, label: string) => Promise<boolean>;
  onCancel: () => void;
}> = ({ edge, name, onSave, onCancel }) => {
  const geo = useEdgePath(edge.source_id, edge.target_id);
  const [value, setValue] = useState(edge.label);
  const [busy, setBusy] = useState(false);
  // Enter, Escape and a blur can all land on one close; only the first counts.
  const done = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  // The field mounts only once both ends are measured, so focus waits for that.
  const measured = !!geo;

  useLayoutEffect(() => {
    input.current?.focus({ preventScroll: true });
    input.current?.select();
  }, [measured]);

  const save = async () => {
    if (done.current) return;
    done.current = true;
    setBusy(true);
    const ok = await onSave(edge.id, value.trim());
    if (!ok) {
      done.current = false;
      setBusy(false);
      input.current?.focus({ preventScroll: true });
    }
  };
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    onCancel();
  };

  if (!geo) return null;
  return (
    <div
      // A control: the pane's pan, marquee and focus-grab leave the pointer to the field.
      data-canvas-control
      className="np-edge-label-editor"
      style={{ transform: `translate(${geo.labelX}px, ${geo.labelY}px) translate(-50%, -50%)` }}
    >
      <input
        ref={input}
        type="text"
        value={value}
        maxLength={200}
        disabled={busy}
        aria-label={`Label for the ${name.charAt(0).toLowerCase()}${name.slice(1)}`}
        aria-describedby={`${edge.id}-label-hint`}
        placeholder="Label this arrow"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Every key stays in the field: the canvas's shortcuts (Delete, the menu key) are not for typing.
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        onBlur={() => {
          if (value.trim() === edge.label.trim()) cancel();
          else save();
        }}
        className="np-edge-label-field"
      />
      <span id={`${edge.id}-label-hint`} className="sr-only">
        Enter saves, Escape cancels. Leave it empty to clear the label.
      </span>
    </div>
  );
};

const Arrow: React.FC<{ id: string; className: string }> = ({ id, className }) => (
  // Sized in stroke units, so the head scales with the edge and one marker serves every zoom.
  <marker
    id={id}
    viewBox="-10 -10 20 20"
    refX="0"
    refY="0"
    markerWidth="12.5"
    markerHeight="12.5"
    markerUnits="strokeWidth"
    orient="auto-start-reverse"
  >
    <path className={className} d="M-7,-5 L0,0 L-7,5 Z" />
  </marker>
);

interface EdgeLayerProps {
  edges: PlanEdge[];
  markerId: string;
  /** Topic titles by node id, for each arrow's accessible name. */
  titles: Map<string, string>;
  /** The arrow whose label is being written, or null. */
  labeling: string | null;
  /** May this person label this arrow: the same people who may remove it. */
  canLabel: (id: string) => boolean;
  /** Opens the label editor; a double-click on a pill calls it. */
  onLabel: (id: string) => void;
  /** Resolves true once the label is saved and the editor may close. */
  onSaveLabel: (id: string, label: string) => Promise<boolean>;
  onCancelLabel: () => void;
}

const arrowName = (e: PlanEdge, titles: Map<string, string>) => {
  const base = `Arrow from ${titles.get(e.source_id) ?? 'a topic'} to ${titles.get(e.target_id) ?? 'a topic'}`;
  const label = e.label.trim();
  return label ? `${base}, labeled ${label}` : base;
};

/**
 * Every arrow in one shared SVG, plus an HTML layer for label pills. Rendered after the
 * nodes so Tab reaches arrows after topics; the nodes' z-index keeps them painted on top.
 */
const EdgeLayer: React.FC<EdgeLayerProps> = ({
  edges,
  markerId,
  titles,
  labeling,
  canLabel,
  onLabel,
  onSaveLabel,
  onCancelLabel,
}) => (
  <>
    <svg className="np-edges">
      <defs>
        <Arrow id={markerId} className="np-arrow" />
        <Arrow id={`${markerId}-sel`} className="np-arrow np-arrow-selected" />
        <Arrow id={`${markerId}-live`} className="np-arrow np-arrow-live" />
      </defs>
      {edges.map((e) => (
        <Edge key={e.id} edge={e} markerId={markerId} name={arrowName(e, titles)} />
      ))}
    </svg>
    <div className="np-edge-labels">
      {edges
        .filter((e) => e.label.trim() || e.id === labeling)
        .map((e) =>
          e.id === labeling ? (
            <EdgeLabelEditor
              key={`${e.id}-editor`}
              edge={e}
              name={arrowName({ ...e, label: '' }, titles)}
              onSave={onSaveLabel}
              onCancel={onCancelLabel}
            />
          ) : (
            <EdgeLabel key={e.id} edge={e} onEdit={canLabel(e.id) ? onLabel : undefined} />
          ),
        )}
    </div>
  </>
);

/** The line that follows the pointer while an arrow is being drawn. Sits above the nodes. */
export const ConnectionLine: React.FC<{ markerId: string }> = ({ markerId }) => {
  const c = useCanvas((s) => s.connecting);
  const origin = useCanvas((s) => (s.connecting ? s.nodes[s.connecting.sourceId] : undefined));
  const snapped = useCanvas((s) => (s.connecting?.target ? s.nodes[s.connecting.target.nodeId] : undefined));
  if (!c || !origin?.measured) return null;
  const from = handlePosition(origin, c.fromSide);
  const to = c.target && c.valid && snapped?.measured ? handlePosition(snapped, c.target.side) : c.pointer;
  // The bezier runs from a right handle to a left one; drawn from a left handle, the ends swap,
  // so the path always ends at the arrow's target.
  const geo = c.fromSide === 'right' ? bezierPath(from.x, from.y, to.x, to.y) : bezierPath(to.x, to.y, from.x, from.y);
  const state = c.valid === null ? '' : c.valid ? 'np-connection-valid' : 'np-connection-invalid';
  return (
    <svg className="np-connection" aria-hidden="true">
      <path
        className={`np-connection-line ${state}`}
        d={geo.path}
        markerEnd={c.valid ? `url(#${markerId}-live)` : undefined}
      />
    </svg>
  );
};

export default EdgeLayer;
